import React, { useEffect, useState } from "react";
import axios from "../api/axiosInstance";
import Modal from "react-modal";
import {
  FaSearch,
  FaFilter,
  FaFileExcel,
  FaEdit,
  FaTimes,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaChevronLeft,
  FaChevronRight,
  FaImage,
  FaRedo,
} from "react-icons/fa";

Modal.setAppElement("#root"); // Required for accessibility

const modalOverlayClass =
  "fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4";
const modalContentBase = "bg-white rounded-2xl shadow-2xl outline-none w-full";

const MembershipPurchase = () => {
  const [purchases, setPurchases] = useState([]);
  const [planOptions, setPlanOptions] = useState([]);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [dates, setDates] = useState({
    startDate: "",
    endDate: "",
  });

  const [filters, setFilters] = useState({
    username: "",
    plan_name: "",
    is_approved: "",
  });

  const [filterText, setFilterText] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    return parseInt(localStorage.getItem("pageSize")) || 10;
  });
  const [count, setCount] = useState(0);

  // 🔹 States for Modals
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [isActiveValue, setIsActiveValue] = useState(null);

  useEffect(() => {
    fetchPurchases();
  }, [filters, page, pageSize, filterText]);

  useEffect(() => {
    fetchPlanOptions();
  }, [filterText, page, pageSize]);

  const totalPages = Math.ceil(count / pageSize) || 1;

  const fetchPlanOptions = async () => {
    try {
      const params = {
        search: filterText,
        page: page,
        page_size: pageSize,
        ...filters, // Merge filter params
      };
      const res = await axios.get("/api/memberships/membership-plans/");
      setPlanOptions(res.data);
    } catch (error) {
      console.error("Error fetching plans:", error);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const fetchPurchases = async () => {
    try {
      const params = new URLSearchParams({
        ...filters,
        search: filterText,
        page,
        page_size: pageSize,
      });

      const res = await axios.get(
        `/api/memberships/membership-purchases/?${params}`,
      );
      setPurchases(res.data.results); // ✅ take only results
      setCount(res.data.count); // ✅ set total count for pagination
    } catch (error) {
      console.error("Error fetching purchases:", error);
    }
  };

  const handleChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const applyFilters = () => {
    setPage(1);
    fetchPurchases();
  };

  const resetFilters = () => {
    setFilters({ username: "", plan_name: "", is_approved: "" });
    setPage(1);
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  // 🔹 Image Modal Open
  const openImageModal = (imgUrl) => {
    setSelectedImage(imgUrl);
    setIsImageModalOpen(true);
  };

  // 🔹 Edit Modal Open
  const openEditModal = (purchase) => {
    setSelectedPurchase(purchase);
    if (purchase.is_approved === true) setIsActiveValue("approve");
    else if (purchase.is_approved === false) setIsActiveValue("reject");
    else setIsActiveValue("pending");
    setIsEditModalOpen(true);
  };

  // 🔹 Save Updated Status
  const handleSaveStatus = async () => {
    let valueToSend = null;
    if (isActiveValue === "approve") valueToSend = true;
    else if (isActiveValue === "reject") valueToSend = false;
    else valueToSend = null;

    try {
      await axios.patch(
        `/api/memberships/membership-purchases/${selectedPurchase.id}/`,
        {
          is_approved: valueToSend,
        },
      );
      setIsEditModalOpen(false);
      fetchPurchases();
    } catch (err) {
      console.error("Error updating status:", err);
    }
  };

  const handleDownload = async (type) => {
    try {
      let url = "/api/memberships/export-membership-purchases/";
      if (type === "filtered" && dates.startDate && dates.endDate) {
        url += `?start_date=${dates.startDate}&end_date=${dates.endDate}`;
      }

      const response = await axios.get(url, { responseType: "blob" });

      const file = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(file);
      link.setAttribute("download", "matrimony_profiles.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      setShowModal(false);
    } catch (error) {
      console.error("Export error:", error);
    }
  };

  // 🔹 Status badge renderer
  const StatusBadge = ({ value }) => {
    if (value === true) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
          <FaCheckCircle className="text-green-600" /> Accepted
        </span>
      );
    }
    if (value === false) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
          <FaTimesCircle className="text-red-600" /> Rejected
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
        <FaClock className="text-amber-600" /> Pending
      </span>
    );
  };

  // 🔹 Windowed pagination numbers (avoids rendering 100s of buttons)
  const getPageNumbers = () => {
    const pages = [];
    const delta = 1;
    const left = Math.max(2, page - delta);
    const right = Math.min(totalPages - 1, page + delta);

    pages.push(1);
    if (left > 2) pages.push("ellipsis-left");
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push("ellipsis-right");
    if (totalPages > 1) pages.push(totalPages);

    return pages;
  };

  return (
    <div className="min-h-screen bg-slate-50 p-3 ml-36 sm:p-5 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-800">
              Membership Purchases
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Review, approve, and export membership purchase records.
            </p>
          </div>
          <div className="text-sm text-slate-500">
            <span className="font-semibold text-slate-700">{count}</span> total
            purchases
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-5">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
              <input
                type="text"
                placeholder="Search by name, email or phone..."
                className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                value={filterText}
                onChange={(e) => {
                  setPage(1);
                  setFilterText(e.target.value);
                }}
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {/* Items per page */}
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <label htmlFor="pageSize" className="whitespace-nowrap">
                  Per page
                </label>
                <select
                  id="pageSize"
                  value={pageSize}
                  onChange={(e) => {
                    setPage(1);
                    setPageSize(Number(e.target.value));
                    localStorage.setItem("pageSize", e.target.value);
                  }}
                  className="border border-slate-300 rounded-lg px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>

              {/* Filters Toggle */}
              <button
                onClick={() => setFiltersVisible(!filtersVisible)}
                className={`relative inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  filtersVisible
                    ? "bg-cyan-600 text-white"
                    : "bg-cyan-50 text-cyan-700 hover:bg-cyan-100"
                }`}
              >
                <FaFilter className="text-sm" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-orange-500 text-white text-[10px] font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </button>

              {/* Export */}
              <button
                onClick={() => setShowModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-green-600 hover:bg-green-700 text-white transition-colors"
              >
                <FaFileExcel className="text-sm" />
                Export
              </button>
            </div>
          </div>

          {/* Filters Panel */}
          {filtersVisible && (
            <div className="mt-4 pt-4 border-t border-slate-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block mb-1.5 text-xs font-medium text-slate-600">
                    Username
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={filters.username}
                    onChange={handleChange}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    placeholder="Enter username"
                  />
                </div>

                <div>
                  <label className="block mb-1.5 text-xs font-medium text-slate-600">
                    Plan Name
                  </label>
                  <select
                    name="plan_name"
                    value={filters.plan_name}
                    onChange={handleChange}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  >
                    <option value="">All Plans</option>
                    {planOptions.map((plan) => (
                      <option key={plan.id} value={plan.name}>
                        {plan.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sm:col-span-2 lg:col-span-2 flex items-end gap-2">
                  <button
                    onClick={applyFilters}
                    className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    Apply Filters
                  </button>
                  <button
                    onClick={resetFilters}
                    className="inline-flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-600 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    <FaRedo className="text-xs" /> Reset
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Desktop Table (lg and up) */}
        <div className="hidden lg:block bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  <th className="py-3 px-4">Username</th>

                  <th className="py-3 px-4">Plan</th>
                  <th className="py-3 px-4">Price</th>
                  <th className="py-3 px-4">Purchased</th>
                  <th className="py-3 px-4">Expires</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Screenshot</th>
                  <th className="py-3 px-4 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {purchases.length === 0 && (
                  <tr>
                    <td
                      colSpan={10}
                      className="py-8 text-center text-slate-400"
                    >
                      No purchases found.
                    </td>
                  </tr>
                )}
                {purchases.map((item, index) => (
                  <tr
                    key={index}
                    className="hover:bg-slate-50 transition-colors"
                  >
                    <td className="py-3 px-4 font-medium text-slate-700">
                      {item.username}
                    </td>
                    <td className="py-3 px-4 text-slate-600">
                      {item.plan_name}
                    </td>
                    <td className="py-3 px-4 font-semibold text-slate-700">
                      ₹{item.discounted_price.toFixed(2)}
                    </td>
                    <td className="py-3 px-4 text-slate-500 whitespace-nowrap">
                      {new Date(item.purchased_at).toLocaleString()}
                    </td>
                    <td className="py-3 px-4 text-slate-500 whitespace-nowrap">
                      {item.expires_at
                        ? new Date(item.expires_at).toLocaleString()
                        : "Unlimited"}
                    </td>
                    <td className="py-3 px-4">
                      <StatusBadge value={item.is_approved} />
                    </td>
                    <td className="py-3 px-4">
                      {item.payment_screenshot ? (
                        <button
                          onClick={() =>
                            openImageModal(
                              `http://localhost:8000${item.payment_screenshot}`,
                            )
                          }
                          className="group relative"
                        >
                          <img
                            src={`http://localhost:8000${item.payment_screenshot}`}
                            alt="Payment Screenshot"
                            className="w-11 h-11 object-cover rounded-lg border border-slate-200 group-hover:opacity-80 transition-opacity"
                          />
                          <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 rounded-lg transition-colors">
                            <FaImage className="text-white text-xs opacity-0 group-hover:opacity-100" />
                          </span>
                        </button>
                      ) : (
                        <span className="text-slate-400 text-xs">No Photo</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={() => openEditModal(item)}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-cyan-600 hover:bg-cyan-50 transition-colors"
                        title="Edit status"
                      >
                        <FaEdit />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile / Tablet Cards (below lg) */}
        <div className="lg:hidden space-y-3">
          {purchases.length === 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 py-10 text-center text-slate-400">
              No purchases found.
            </div>
          )}
          {purchases.map((item, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  {item.payment_screenshot ? (
                    <button
                      onClick={() =>
                        openImageModal(
                          `http://localhost:8000${item.payment_screenshot}`,
                        )
                      }
                      className="flex-shrink-0"
                    >
                      <img
                        src={`http://localhost:8000${item.payment_screenshot}`}
                        alt="Payment Screenshot"
                        className="w-12 h-12 object-cover rounded-lg border border-slate-200"
                      />
                    </button>
                  ) : (
                    <div className="w-12 h-12 flex-shrink-0 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 text-[10px] text-center">
                      No Photo
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800 truncate">
                      {item.username}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {item.first_name} {item.last_name}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => openEditModal(item)}
                  className="flex-shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg text-cyan-600 hover:bg-cyan-50 transition-colors"
                  title="Edit status"
                >
                  <FaEdit />
                </button>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-y-2 text-sm">
                <div>
                  <p className="text-xs text-slate-400">Plan</p>
                  <p className="text-slate-700 font-medium">{item.plan_name}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Price</p>
                  <p className="text-slate-700 font-medium">
                    ₹{item.discounted_price.toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Purchased</p>
                  <p className="text-slate-600">
                    {new Date(item.purchased_at).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Expires</p>
                  <p className="text-slate-600">
                    {item.expires_at
                      ? new Date(item.expires_at).toLocaleDateString()
                      : "Unlimited"}
                  </p>
                </div>
              </div>

              <div className="mt-3">
                <StatusBadge value={item.is_approved} />
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-slate-500 text-center sm:text-left">
            Showing page{" "}
            <span className="font-semibold text-slate-700">{page}</span> of{" "}
            <span className="font-semibold text-slate-700">{totalPages}</span>
          </p>
          <div className="flex items-center justify-center gap-1.5 flex-wrap">
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-slate-100 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <FaChevronLeft className="text-xs text-slate-600" />
            </button>

            {getPageNumbers().map((p, i) =>
              p === "ellipsis-left" || p === "ellipsis-right" ? (
                <span key={`${p}-${i}`} className="px-2 text-slate-400 text-sm">
                  …
                </span>
              ) : (
                <button
                  key={p}
                  onClick={() => handlePageChange(p)}
                  className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                    page === p
                      ? "bg-cyan-600 text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {p}
                </button>
              ),
            )}

            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages}
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-slate-100 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <FaChevronRight className="text-xs text-slate-600" />
            </button>
          </div>
        </div>
      </div>

      {/* 🔹 IMAGE MODAL */}
      <Modal
        isOpen={isImageModalOpen}
        onRequestClose={() => setIsImageModalOpen(false)}
        className={`${modalContentBase} max-w-lg p-4`}
        overlayClassName={modalOverlayClass}
      >
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-semibold text-slate-700">Payment Screenshot</h3>
          <button
            onClick={() => setIsImageModalOpen(false)}
            className="text-slate-400 hover:text-slate-600"
          >
            <FaTimes />
          </button>
        </div>
        {selectedImage && (
          <img
            src={selectedImage}
            alt="Payment Screenshot"
            className="w-full rounded-lg border border-slate-200"
          />
        )}
      </Modal>

      {/* 🔹 EDIT MODAL */}
      <Modal
        isOpen={isEditModalOpen}
        onRequestClose={() => setIsEditModalOpen(false)}
        className={`${modalContentBase} max-w-md p-6`}
        overlayClassName={modalOverlayClass}
      >
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-lg font-bold text-slate-800">Update Status</h2>
          <button
            onClick={() => setIsEditModalOpen(false)}
            className="text-slate-400 hover:text-slate-600"
          >
            <FaTimes />
          </button>
        </div>

        <div className="mb-6">
          <label className="block mb-2 text-sm font-medium text-slate-600">
            Approval Status
          </label>
          <div className="grid grid-cols-3 gap-2">
            {[
              {
                value: "pending",
                label: "Pending",
                icon: FaClock,
                color: "amber",
              },
              {
                value: "approve",
                label: "Accept",
                icon: FaCheckCircle,
                color: "green",
              },
              {
                value: "reject",
                label: "Reject",
                icon: FaTimesCircle,
                color: "red",
              },
            ].map(({ value, label, icon: Icon, color }) => (
              <button
                key={value}
                type="button"
                onClick={() => setIsActiveValue(value)}
                className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 text-sm font-medium transition-colors ${
                  isActiveValue === value
                    ? color === "green"
                      ? "border-green-500 bg-green-50 text-green-700"
                      : color === "red"
                        ? "border-red-500 bg-red-50 text-red-700"
                        : "border-amber-500 bg-amber-50 text-amber-700"
                    : "border-slate-200 text-slate-500 hover:bg-slate-50"
                }`}
              >
                <Icon />
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={() => setIsEditModalOpen(false)}
            className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-sm font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveStatus}
            className="px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-medium transition-colors"
          >
            Save Changes
          </button>
        </div>
      </Modal>

      {/* 🔹 EXPORT MODAL */}
      <Modal
        isOpen={showModal}
        onRequestClose={() => setShowModal(false)}
        className={`${modalContentBase} max-w-sm p-6`}
        overlayClassName={modalOverlayClass}
      >
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-lg font-bold text-slate-800">Export Profiles</h2>
          <button
            onClick={() => setShowModal(false)}
            className="text-slate-400 hover:text-slate-600"
          >
            <FaTimes />
          </button>
        </div>

        <div className="space-y-3 mb-5">
          <div>
            <label className="block mb-1.5 text-xs font-medium text-slate-600">
              Start Date
            </label>
            <input
              type="date"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
              value={dates.startDate}
              onChange={(e) =>
                setDates({ ...dates, startDate: e.target.value })
              }
            />
          </div>

          <div>
            <label className="block mb-1.5 text-xs font-medium text-slate-600">
              End Date
            </label>
            <input
              type="date"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
              value={dates.endDate}
              onChange={(e) => setDates({ ...dates, endDate: e.target.value })}
            />
          </div>
        </div>

        <div className="space-y-2">
          <button
            onClick={() => handleDownload("filtered")}
            disabled={!dates.startDate || !dates.endDate}
            className="w-full inline-flex items-center justify-center gap-2 bg-cyan-600 hover:bg-cyan-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
          >
            <FaFileExcel /> Download Filtered
          </button>

          <button
            onClick={() => handleDownload("all")}
            className="w-full inline-flex items-center justify-center gap-2 bg-slate-600 hover:bg-slate-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
          >
            <FaFileExcel /> Download All
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default MembershipPurchase;
